<template>
    <div class="platforms-container">
      <h1>Auction Platforms</h1>
  
      <!-- Filters -->
      <div class="filters">
        <input type="text" v-model="filters.name" placeholder="Search by Name..." class="search-input"/>
        <input type="text" v-model="filters.location" placeholder="Search by Location..." class="search-input"/>
      </div>
  
      <button @click="openAddPopup" class="add-button">Add Platform</button>
  
      <!-- Platforms Table -->
      <div class="table-container">
        <table class="platforms-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Location</th>
              <th>Contact Info</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="platform in filteredPlatforms" :key="platform.auction_house_id">
              <td>{{ platform.name }}</td>
              <td>{{ platform.location }}</td>
              <td>
                <a v-if="platform.contact_info" 
                    :href="platform.contact_info.startsWith('http') ? platform.contact_info : 'https://' + platform.contact_info" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    {{ platform.contact_info }}
                </a>
                <span v-else>N/A</span>
                </td>

              <td class="action-buttons">
                <button @click="openEditPopup(platform)" class="edit-button">Edit</button>
                <button @click="confirmDelete(platform)" class="delete-button">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
  
      <!-- Add/Edit Platform Popup -->
      <div v-if="showPopup" class="popup-container">
        <div class="popup">
          <h2 class="popup-title">{{ editing ? 'Edit Platform' : 'Add Platform' }}</h2>
          <form @submit.prevent="submitPlatform" class="popup-form">
            
            <label class="popup-label">Platform Name:
              <input type="text" v-model="currentPlatform.name" required class="popup-input" />
            </label>
  
            <label class="popup-label">Location:
              <select v-model="currentPlatform.location" required class="popup-select">
                <option value="" disabled>Select a location type</option>
                <option>Online</option>
                <option>Physical</option>
                <option>Multiple Locations</option>
                <option>Local</option>
              </select>
            </label>
  
            <label class="popup-label">Contact Info:
              <input type="text" v-model="currentPlatform.contact_info" placeholder="Website, phone, etc." class="popup-input" />
            </label>
  
            <div class="popup-buttons">
              <button type="submit" class="save-button">Save</button>
              <button type="button" @click="showPopup = false" class="cancel-button">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import api from '@/services/api';
  
  export default {
    data() {
      return {
        auctionHouses: [],
        filters: { name: "", location: "" },
        showPopup: false,
        editing: false,
        currentPlatform: { auction_house_id: null, name: "", location: "", contact_info: "" },
      };
    },
    computed: {
      filteredPlatforms() {
        return this.auctionHouses.filter((platform) => 
          (!this.filters.name || platform.name.toLowerCase().includes(this.filters.name.toLowerCase())) &&
          (!this.filters.location || platform.location.toLowerCase().includes(this.filters.location.toLowerCase()))
        );
      },
    },
    mounted() {
      this.fetchPlatforms();
    },
    methods: {
      async fetchPlatforms() {
        const response = await api.get("/auction-houses");
        this.auctionHouses = response.data;
      },
      openAddPopup() {
        this.currentPlatform = { auction_house_id: null, name: "", location: "", contact_info: "" };
        this.editing = false;
        this.showPopup = true;
      },
      openEditPopup(platform) {
        this.currentPlatform = { ...platform };
        this.editing = true;
        this.showPopup = true;
      },
      async submitPlatform() {
        if (this.editing) {
          await api.put(`/auction-houses/${this.currentPlatform.auction_house_id}`, this.currentPlatform);
        } else {
          await api.post("/auction-houses", this.currentPlatform);
        }
        this.showPopup = false;
        this.fetchPlatforms();
      },
      async confirmDelete(platform) {
        if (confirm(`Are you sure you want to delete "${platform.name}"?`)) {
          await api.delete(`/auction-houses/${platform.auction_house_id}`);
          this.fetchPlatforms();
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .platforms-container {
    padding: 20px;
    font-family: "Poppins", sans-serif !important;
    background: #9cd0f2;
    border-radius: 10px;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .filters {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
  }
  
  .search-input {
    padding: 8px;
    border-radius: 5px;
    border: 1px solid #b0c4de;
    background: #e6eef3;
  }
  
  .add-button, .save-button, .cancel-button, .edit-button, .delete-button {
    padding: 10px;
    border-radius: 5px;
    cursor: pointer;
    font-weight: bold;
  }
  
  .add-button { background-color: #288671; color: white; }
  .add-button:hover { background-color: #237a4b; }
  
  .save-button { background-color: #327ba8; color: white; }
  .save-button:hover { background-color: #286c98; }
  
  .cancel-button { background-color: #4f6773; color: white; }
  .cancel-button:hover { background-color: #3a4a5e; }
  
  .edit-button { background-color: #3a4a5e; color: white; }
  .edit-button:hover { background-color: #2a3a4a; }
  
  .delete-button { background-color: #9f3207; color: white; }
  .delete-button:hover { background-color: #1f2a35; }
  
  .table-container {
    background: #eef4fb;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .platforms-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    border-radius: 10px;
    overflow: hidden;
  }
  
  .platforms-table th, .platforms-table td {
    border: 1px solid #83a7cb;
    padding: 8px;
    text-align: center;
  }
  
  .platforms-table tr:hover { background-color: #e0f2f1; }
  
  .popup-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .popup {
    background: white;
    padding: 20px;
    border-radius: 10px;
    width: 50vw;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.3);
  }
  
  .popup-title {
    margin-bottom: 15px;
    font-size: 1.5rem;
    font-weight: bold;
  }
  
  .popup-form {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  
  .popup-label {
    display: flex;
    flex-direction: column;
    font-weight: bold;
    margin-bottom: 10px;
  }
  
  .popup-input, .popup-select {
    padding: 8px;
    border-radius: 5px;
    border: 1px solid #b0c4de;
    background: #e6eef3;
    width: 100%;
  }
  
  .popup-buttons {
    display: flex;
    justify-content: space-between;
    margin-top: 15px;
  }
  </style>
  